import { useMemo, useState } from 'react';
import { EyeIcon, EyeSlashIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { MarkdownBody } from './MarkdownBody';
import { AttachmentRow } from './AttachmentRow';
import { useUpdateComment } from '../../hooks/useUpdateComment';
import { useDeleteComment } from '../../hooks/useDeleteComment';
import { useUpdateCommentVisibility } from '../../hooks/useUpdateCommentVisibility';
import type { CommentDTO } from '../../services/api/comments';
import { useRobotEnabled } from '../../hooks/useRobotEnabled'; // MKS-338

/**
 * Phase 25 Plan 25-05 — single comment renderer.
 *
 * Affordances per CMT-04 + D-12:
 *   - Pencil (edit): visible iff the actor is the comment author. Admins do
 *     NOT get an edit affordance (CMT-04 — admins delete, not edit).
 *   - Trash (delete): visible iff the actor is the author OR an internal
 *     admin. External non-authors NEVER see the trash, even if they have
 *     project visibility.
 *   - Eye / eye-slash (MKS-185): flips the comment between internal-only and
 *     public. Internal actors only, author or admin, internally-authored
 *     comments only — see `canChangeVisibility`.
 *
 * Edit (D-13): in-place — the bubble swaps to a textarea pre-filled with the
 * raw markdown. Save calls PATCH; Cancel reverts without an API call.
 *
 * Delete (D-15): native window.confirm() — project ESLint config allows it
 * (frontend/eslint.config.js globals: 'confirm').
 *
 * (edited) tag (D-14): when updatedAt > createdAt, append `(edited)` after
 * the timestamp with a `title` tooltip carrying the ISO updatedAt.
 */
export type BubbleActor =
  | { kind: 'user'; id: number; isAdmin: boolean }
  | { kind: 'external_user'; id: number };

export interface AuthorLookup {
  /** Optional display name for the author user; falls back to email or generic label. */
  displayName?: string;
  email?: string;
}

export interface CommentBubbleProps {
  taskId: number;
  comment: CommentDTO;
  actor: BubbleActor;
  authorLookup?: AuthorLookup;
}

export function CommentBubble({ taskId, comment, actor, authorLookup }: CommentBubbleProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [editBody, setEditBody] = useState(comment.body);
  const updateMutation = useUpdateComment();
  const deleteMutation = useDeleteComment();
  const visibilityMutation = useUpdateCommentVisibility();
  // MKS-338 — the Factory™ attribution badge belongs to the robot add-on.
  const robotEnabled = useRobotEnabled();

  const canEdit = useMemo(() => {
    if (actor.kind === 'user') return comment.authorUserId === actor.id;
    return comment.authorExternalUserId === actor.id;
  }, [actor, comment]);

  const canDelete = useMemo(() => {
    if (canEdit) return true;
    if (actor.kind === 'user' && actor.isAdmin) return true;
    return false;
  }, [canEdit, actor]);

  /**
   * MKS-185 — who sees the visibility toggle. Mirrors the backend guard in
   * `taskCommentService.updateCommentVisibility` exactly, which is the real
   * enforcement boundary; this only hides the button.
   *   - internal actors only (an external user's own comment is public by
   *     construction and they must not be able to publish an internal one),
   *   - the author or an internal admin — the same set that may delete,
   *   - internally-authored comments only.
   */
  const canChangeVisibility = useMemo(() => {
    if (actor.kind !== 'user') return false;
    if (comment.authorUserId === null) return false;
    return comment.authorUserId === actor.id || actor.isAdmin;
  }, [actor, comment]);

  const isEdited =
    new Date(comment.updatedAt).getTime() > new Date(comment.createdAt).getTime();

  const displayName =
    authorLookup?.displayName ?? authorLookup?.email ?? deriveAuthorName(comment);

  function handleSave() {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    if (trimmed === comment.body) {
      setMode('view');
      return;
    }
    updateMutation.mutate(
      { taskId, commentId: comment.id, body: trimmed },
      {
        onSuccess: () => setMode('view'),
      },
    );
  }

  function handleDelete() {
    // D-15 — native confirm (project ESLint allowlist permits).
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    deleteMutation.mutate({ taskId, commentId: comment.id });
  }

  /**
   * MKS-185 — both directions are confirmed, for different reasons: publishing
   * shows the comment to the customer, and hiding it cannot recall the mail
   * external watchers already received. Native confirm, same affordance as the
   * delete above (D-15).
   */
  function handleToggleVisibility() {
    const next = !comment.isInternalOnly;
    const prompt = next
      ? 'Hide this comment from the customer portal? Any email already sent about it cannot be recalled.'
      : 'Make this comment visible to the customer in the portal?';
    if (!window.confirm(prompt)) return;
    visibilityMutation.mutate({ taskId, commentId: comment.id, isInternalOnly: next });
  }

  return (
    <article
      className="group relative rounded-lg border border-gray-200 bg-white p-3 focus-within:ring-2 focus-within:ring-primary-200"
      tabIndex={-1}
    >
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {displayName}
          </span>
          <span className="text-xs text-gray-500" title={comment.createdAt}>
            · {formatRelative(comment.createdAt)}
          </span>
          {isEdited && (
            <span
              className="text-xs text-gray-400 italic"
              title={`Edited ${comment.updatedAt}`}
            >
              · (edited)
            </span>
          )}
          {/*
           * Phase 114 (Plan 114-04 / PCEXEC-01) — Factory™ attribution
           * badge. Explicit equality check against the single known value
           * ('paperclip'), never a truthiness or raw-value render: a future
           * 'monka' value (pre-authorised by the CHECK constraint but
           * deliberately never written this milestone, D-114-04) must not
           * silently start rendering an unstyled string here.
           *
           * D-114-10 explicitly refuses a "frozen"/read-only/disabled
           * affordance for a Factory™-sourced comment — edits and deletes
           * never propagate to Factory™, divergence is accepted and
           * documented, and this badge is the ONLY visual difference. The
           * edit/delete permission memos above are computed identically
           * regardless of sourceExecutor. Do not add a disabled state here
           * as an "obvious" follow-up.
           */}
          {robotEnabled && comment.sourceExecutor === 'paperclip' && (
            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
              Factory™
            </span>
          )}
          {comment.isInternalOnly && (
            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
              Internal only
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
          {canEdit && mode === 'view' && (
            <button
              type="button"
              onClick={() => {
                setEditBody(comment.body);
                setMode('edit');
              }}
              aria-label="Edit comment"
              className="text-gray-400 hover:text-gray-700"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          {canChangeVisibility && mode === 'view' && (
            <button
              type="button"
              onClick={handleToggleVisibility}
              disabled={visibilityMutation.isPending}
              aria-label={
                comment.isInternalOnly ? 'Make comment public' : 'Make comment internal only'
              }
              title={
                comment.isInternalOnly
                  ? 'Publish this comment to the customer portal'
                  : 'Hide this comment from the customer portal'
              }
              className="text-gray-400 hover:text-gray-700"
            >
              {comment.isInternalOnly ? (
                <EyeIcon className="w-4 h-4" />
              ) : (
                <EyeSlashIcon className="w-4 h-4" />
              )}
            </button>
          )}
          {canDelete && mode === 'view' && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              aria-label="Delete comment"
              className="text-gray-400 hover:text-red-600"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      {mode === 'edit' ? (
        <div className="space-y-2">
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={3}
            className="w-full text-sm text-gray-900 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            aria-label="Edit comment body"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setMode('view')}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={editBody.trim().length === 0 || updateMutation.isPending}
              isLoading={updateMutation.isPending}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <MarkdownBody body={comment.body} />
          <AttachmentRow
            taskId={taskId}
            commentId={comment.id}
            attachments={comment.attachments}
          />
        </>
      )}
    </article>
  );
}

function deriveAuthorName(comment: CommentDTO): string {
  const full = [comment.authorFirstName, comment.authorLastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (comment.authorEmail) return comment.authorEmail;
  if (comment.authorUserId !== null) return `User #${comment.authorUserId}`;
  if (comment.authorExternalUserId !== null) {
    return `External #${comment.authorExternalUserId}`;
  }
  return 'Unknown';
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
